import { useEffect, useRef } from "react";
import { Download } from "lucide-react";
import { scrollToSection, useScrollSpy } from "./lib";
import { IMG, PDF } from "./content";

export const SECTIONS = [
  { id: "highlights", label: "2025 Highlights", short: "Highlights", color: "var(--am-blue)" },
  { id: "people", label: "Empowering People", short: "People", color: "var(--am-purple)" },
  { id: "planet", label: "Environmental Progress", short: "Environment", color: "var(--am-green)" },
  { id: "data", label: "Data and Disclosures", short: "Data", color: "var(--am-blue)" },
];

function PdfButton({ testId }: { testId: string }) {
  return (
    <a href={PDF} target="_blank" rel="noreferrer" data-testid={testId}
      className="inline-flex shrink-0 items-center gap-2 rounded-full bg-[var(--am-blue)] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[var(--am-blue-dark)]">
      <Download size={15} aria-hidden /> <span className="hidden sm:inline">Full report</span> PDF
      <span className="sr-only"> (opens in a new tab)</span>
    </a>
  );
}

export function AmatHeader() {
  const active = useScrollSpy(SECTIONS.map((s) => s.id));
  const ref = useRef<HTMLElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const set = () => document.documentElement.style.setProperty("--am-head", `${el.offsetHeight - 1}px`);
    set();
    const ro = new ResizeObserver(set);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return (
    <header ref={ref} className="sticky top-0 z-40 border-b border-[var(--am-line)] bg-white/90 backdrop-blur-md" data-testid="am-header">
      <div className="bg-[var(--am-gray)] px-4 py-1.5 text-center text-xs text-white" data-testid="am-example-banner">
        <strong className="mr-2 rounded-full bg-white px-2 py-0.5 text-[11px] uppercase tracking-wider text-[var(--am-gray)]">Example</strong>
        Sample review build by Big Pivot Partners, not an official Applied Materials publication. All content is taken from the 2025 Impact Report PDF.
      </div>
      <div className="mx-auto flex max-w-[1320px] items-center gap-6 px-5 py-3 sm:px-8">
        <a href="#top" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: "smooth" }); }} data-testid="am-logo-link" className="shrink-0">
          <img src={IMG("logo-blue.png")} alt="Applied Materials" className="h-8 w-auto sm:h-9" />
        </a>
        <nav aria-label="Report sections" className="am-scroll-x hidden flex-1 justify-center gap-8 overflow-x-auto md:flex">
          {SECTIONS.map((s) => (
            <button key={s.id} type="button" onClick={() => scrollToSection(s.id)} aria-current={active === s.id}
              className="am-nav-link whitespace-nowrap text-xs font-semibold uppercase tracking-[0.06em]"
              style={{ color: active === s.id ? s.color : "var(--am-gray)" }} data-testid={`am-nav-${s.id}`}>
              {s.label}
            </button>
          ))}
        </nav>
        <div className="ml-auto md:ml-0"><PdfButton testId="am-header-pdf" /></div>
      </div>
      <nav aria-label="Report sections (mobile)" className="am-scroll-x flex justify-between gap-5 overflow-x-auto px-5 pb-2.5 md:hidden">
        {SECTIONS.map((s) => (
          <button key={s.id} type="button" onClick={() => scrollToSection(s.id)} aria-current={active === s.id}
            className="am-nav-link whitespace-nowrap text-xs font-semibold uppercase tracking-[0.06em]"
            style={{ color: active === s.id ? s.color : "var(--am-gray)" }} data-testid={`am-mnav-${s.id}`}>
            {s.short}
          </button>
        ))}
      </nav>
    </header>
  );
}


export function AmatHero() {
  return (
    <section id="top" className="relative isolate min-h-[78vh] overflow-hidden bg-[#1e3442]" data-testid="am-hero">
      <img src={IMG("p1_0.jpg")} alt="Construction of the Equipment and Process Innovation and Commercialization (EPIC) Center"
        className="absolute inset-0 -z-10 h-full w-full object-cover" />
      <div className="absolute inset-0 -z-10 bg-gradient-to-t from-black/70 via-black/25 to-transparent" />
      <div className="mx-auto flex min-h-[78vh] max-w-[1320px] flex-col justify-end px-5 pb-14 sm:px-8 sm:pb-20">
        <span
          className="mb-5 w-fit bg-[var(--am-navy)] px-5 py-2.5 text-base font-semibold uppercase tracking-wide text-white shadow-[7px_6px_0_var(--am-yellow)] sm:text-lg" data-testid="am-hero-tag">
          2025 Impact Report
        </span>
        <h1 className="max-w-3xl text-5xl font-semibold leading-[0.98] tracking-tight text-white sm:text-6xl lg:text-8xl" data-testid="am-hero-title">
          {["Powering", "Sustainable", "Growth"].map((w) => (
            <span key={w} className="block">{w}</span>
          ))}
        </h1>
        <p className="mt-6 max-w-xl text-base text-white/90 sm:text-lg">
          “This marks the 20th year of Applied Materials’ impact reporting. It also marks the midpoint toward our bold 2030 sustainability goals, first announced in 2020.”
        </p>
        <p className="mt-2 text-sm text-white/85">Message From Our CEO</p>
        <p className="mt-6 text-xs text-white/80">On the cover: the EPIC Center, on track to open in late 2026.</p>
      </div>
    </section>
  );
}

export function Divider({ id, number, title, items, image, alt }: { id: string; number: string; title: string; items: string[]; image: string; alt: string }) {
  return (
    <div className="grid md:grid-cols-[minmax(0,38%)_1fr]" data-testid={`am-divider-${id}`}>
      <div className="am-divider-panel am-bg-accent px-6 py-10 text-white sm:px-10 sm:py-14">
        <span className="relative text-4xl font-light sm:text-5xl">{number}</span>
        <h2 className="relative mt-8 text-4xl font-semibold leading-[1.02] sm:text-5xl lg:text-6xl" data-section-heading tabIndex={-1}>{title}</h2>
        <ul className="relative mt-8 max-w-xs text-sm font-semibold">
          {items.map((t) => <li key={t} className="border-b border-white/40 py-2">{t}</li>)}
        </ul>
      </div>
      <div className="am-photo relative min-h-[260px] sm:min-h-[380px]">
        <img src={IMG(image)} alt={alt} className="absolute inset-0 h-full w-full object-cover" loading="lazy" />
        <span className="am-corner" aria-hidden />
      </div>
    </div>
  );
}
